import { useMemo } from "react";
import type { FileItem } from "../../types";
import { Badge } from "@/shared/ui/badge";
import { FileMetadataField } from "./FileMetadataField";
import { format } from "date-fns";
import { useTags } from "@/features/admin/queries";
import { DEFAULT_TAG_COLOR } from "@/services/api/tags";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";

interface FileMetadataSectionProps {
  file: FileItem;
  attributeOrder?: string[];
  categoryAttributes?: DisplayAttribute[];
}

export function FileMetadataSection({
  file,
  attributeOrder,
  categoryAttributes,
}: FileMetadataSectionProps) {
  const formattedModifiedDate = useMemo(() => {
    if (!file.modifiedDate) return "";
    const date = new Date(file.modifiedDate);
    if (Number.isNaN(date.getTime())) return "";
    return format(date, "dd/MM/yyyy 'at' HH:mm:ss");
  }, [file.modifiedDate]);

  const { data: availableTags = [] } = useTags();

  const tagsWithColors = useMemo(() => {
    return file.tags.map((tagName) => {
      const tagObj = availableTags.find((t) => t.name === tagName);
      return {
        name: tagName,
        color: tagObj?.color,
      };
    });
  }, [file.tags, availableTags]);

  const orderedAttributes = useMemo(() => {
    const categoryAttributesMap = new Map(
      (categoryAttributes || []).map((attr) => [attr.name, attr])
    );

    const ordered: Array<{
      attributeName: string;
      value: string;
      isMandatory?: boolean;
    }> = [];
    const seen = new Set<string>();

    if (file.attributeValues && file.attributeValues.length > 0) {
      file.attributeValues.forEach((attr) => {
        const categoryAttr = categoryAttributesMap.get(attr.attributeName);
        ordered.push({
          attributeName: attr.attributeName,
          value: attr.value,
          isMandatory: categoryAttr?.isRequired,
        });
        seen.add(attr.attributeName);
      });
    }

    if (categoryAttributes && categoryAttributes.length > 0) {
      const orderToUse =
        attributeOrder ||
        [...categoryAttributes]
          .sort((a, b) => a.order - b.order)
          .map((attr) => attr.name);

      orderToUse.forEach((attrName) => {
        if (!seen.has(attrName) && categoryAttributesMap.has(attrName)) {
          const categoryAttr = categoryAttributesMap.get(attrName);
          ordered.push({
            attributeName: attrName,
            value: "",
            isMandatory: categoryAttr?.isRequired,
          });
          seen.add(attrName);
        }
      });
    }

    return ordered;
  }, [file.attributeValues, attributeOrder, categoryAttributes]);

  return (
    <div className="space-y-4 px-6 py-4">
      <FileMetadataField label="Type" value={file.type} />
      <FileMetadataField label="Size" value={file.size} />
      <FileMetadataField
        label="Last modified"
        value={formattedModifiedDate || "Not modified"}
      />
      {orderedAttributes.map((attr) => {
        const isEmpty = !attr.value || attr.value.trim() === "";
        const isMandatory = attr.isMandatory ?? false;
        const displayValue = isEmpty ? undefined : attr.value;

        return (
          <FileMetadataField
            key={attr.attributeName}
            label={attr.attributeName}
            value={displayValue}
          >
            {isEmpty && (
              <p className="mt-1 text-sm text-ink-400 italic">
                {isMandatory ? "missing" : "N/A"}
              </p>
            )}
          </FileMetadataField>
        );
      })}
      <FileMetadataField
        label="Tags"
        value={file.tags.length > 0 ? "" : "No tags"}
      >
        {file.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {tagsWithColors.map((tag) => {
              const tagColor = tag.color ?? DEFAULT_TAG_COLOR;
              const isDefaultColor = tagColor === DEFAULT_TAG_COLOR;
              const textColor = isDefaultColor ? "#6B7280" : tagColor;
              return (
                <Badge
                  key={tag.name}
                  variant="outline"
                  className="text-xs font-medium"
                  style={{
                    backgroundColor: `${tagColor}20`,
                    color: textColor,
                    borderColor: `${tagColor}40`,
                  }}
                >
                  {tag.name}
                </Badge>
              );
            })}
          </div>
        )}
      </FileMetadataField>
    </div>
  );
}
